// User Activity: find a user (legacy app or Barsys AI app) and see who they
// are, every drink they crafted, the machines and mixlists they used, and their
// reviews / feedback. Backed by GET /secureApi/analytics/user-activity/*
// (users:access enforced by the API).

import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { DataTable, Section, StateBanner, Tile, fmtInt } from "../../components/bigquery/bqShared.jsx";

const API = `${process.env.REACT_APP_ADMIN_API_ADDRESS}/secureApi/analytics/user-activity`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` } });
const fmtTime = (v) => (v ? String(v).replace("T", " ").slice(0, 16) : "—");
const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");
const TYPE_LABEL = { ai: "Barsys AI app", legacy: "Legacy app" };

function Stars({ value }) {
  if (!value) return <span className="text-inverse text-opacity-50">—</span>;
  return <span title={`${value} / 5`}>{"★".repeat(Math.round(value))}<span className="text-inverse text-opacity-25">{"★".repeat(Math.max(0, 5 - Math.round(value)))}</span></span>;
}

export function ReviewsTable({ rows, showUser }) {
  return (
    <DataTable
      rows={rows}
      empty="No reviews or feedback"
      columns={[
        { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
        { key: "source", label: "Type", render: (r) => r.source_label || r.source },
        ...(showUser
          ? [{ key: "user", label: "User", render: (r) => (r.user_id ? <Link to={`/users/activity?type=${r.user_type}&id=${encodeURIComponent(r.user_id)}`}>{r.user_label || r.user_id}</Link> : r.user_label || "—") }]
          : []),
        { key: "rating", label: "Rating", render: (r) => <Stars value={r.rating} /> },
        { key: "text", label: "What they said", width: 640, render: (r) => <span style={{ whiteSpace: "normal" }}>{r.text || "—"}</span> },
        { key: "context", label: "About", render: (r) => r.context || "—" },
      ]}
    />
  );
}

export default function UserActivity() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type");
  const id = params.get("id");
  const [days, setDays] = useState(90);

  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [searchError, setSearchError] = useState(null);

  const [user, setUser] = useState({ data: null, loading: false, error: null });

  useEffect(() => {
    if (!type || !id) return;
    let cancelled = false;
    setUser((u) => ({ ...u, loading: true, error: null }));
    axios
      .get(`${API}/${type}/${encodeURIComponent(id)}`, { params: { days }, ...auth() })
      .then((r) => !cancelled && setUser({ data: r.data.data, loading: false, error: null }))
      .catch((e) => !cancelled && setUser({ data: null, loading: false, error: e.response?.status === 403 ? "You need the Users permission." : e.response?.data?.message || e.message }));
    return () => {
      cancelled = true;
    };
  }, [type, id, days]);

  const search = (e) => {
    e.preventDefault();
    if (q.trim().length < 3) return;
    setSearching(true);
    setSearchError(null);
    axios
      .get(`${API}/search`, { params: { q: q.trim() }, ...auth() })
      .then((r) => setResults(r.data.data.results))
      .catch((err) => setSearchError(err.response?.data?.message || err.message))
      .finally(() => setSearching(false));
  };

  const d = user.data;
  const p = d?.profile;
  const a = d?.activity;
  const name = p ? (p.type === "ai" ? [p.first_name, p.last_name].filter(Boolean).join(" ") : p.username) || "(no name)" : "";
  const craftCount = a?.summary?.find((s) => s.event === "drink_mixed")?.count;

  return (
    <div>
      <ul className="breadcrumb">
        <li className="breadcrumb-item">USERS</li>
        <li className="breadcrumb-item active">USER ACTIVITY</li>
      </ul>
      <h1 className="page-header">
        User Activity <small>drinks, machines, mixlists, bar, taste and reviews per user</small>
      </h1>

      <Section title="FIND A USER" subtitle="name, email, phone or user id; legacy app and Barsys AI app">
        <form className="d-flex gap-2" onSubmit={search}>
          <input className="form-control" style={{ maxWidth: 420 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. a name, an email, the last digits of a phone number, or an id" />
          <button className="btn btn-theme" disabled={searching || q.trim().length < 3}>{searching ? "Searching…" : "Search"}</button>
        </form>
        {searchError && <div className="alert alert-danger mt-3 mb-0">{searchError}</div>}
        {results && (
          <div className="mt-3">
            <DataTable
              rows={results}
              empty="No users found"
              columns={[
                { key: "type", label: "App", render: (r) => TYPE_LABEL[r.type] },
                { key: "name", label: "Name", render: (r) => r.name || "—" },
                { key: "email", label: "Email", render: (r) => r.email || "—" },
                { key: "phone", label: "Phone", render: (r) => r.phone || "—" },
                { key: "signed_up_at", label: "Signed up", render: (r) => fmtDate(r.signed_up_at) },
                { key: "open", label: "", render: (r) => <button className="btn btn-sm btn-outline-theme" onClick={() => setParams({ type: r.type, id: r.id })}>View activity</button> },
              ]}
            />
          </div>
        )}
      </Section>

      {(type && id) && <StateBanner loading={user.loading} error={user.error} hasData={!!d} />}

      {d && (
        <div style={{ opacity: user.loading ? 0.5 : 1 }}>
          <div className="d-flex align-items-center flex-wrap mb-3">
            <h3 className="mb-0">{name}</h3>
            <span className="badge bg-theme text-theme-color ms-2">{p.app}</span>
            <div className="ms-auto btn-group">
              {[30, 90, 180, 365].map((n) => (
                <button key={n} className={`btn btn-sm ${n === days ? "btn-theme" : "btn-outline-theme"}`} onClick={() => setDays(n)} disabled={user.loading}>{n}d</button>
              ))}
            </div>
          </div>

          <div className="row">
            <Tile label="Email" value={<span className="fs-6">{p.email || "—"}</span>} />
            <Tile label="Phone" value={<span className="fs-6">{p.phone || "—"}</span>} />
            <Tile label="Signed up" value={<span className="fs-6">{fmtDate(p.signed_up_at)}</span>} sub={p.last_login_at ? `last login ${fmtDate(p.last_login_at)}` : p.country ? `country ${p.country}` : null} />
            <Tile label={`Drinks crafted (${a.days}d)`} value={fmtInt(craftCount || 0)} />
            <Tile label="Machines used" value={fmtInt(a.machines.length)} sub={[...new Set(a.machines.map((m) => m.machine).filter(Boolean))].join(", ") || null} />
            <Tile label="Reviews & feedback" value={fmtInt(d.reviews.length)} />
          </div>

          <div className="row">
            <div className="col-xl-8">
              <Section title="DRINKS CRAFTED" subtitle={`newest first, last ${a.days} days${a.crafts_truncated ? " (latest 500)" : ""}`} bodyClass="p-0">
                <DataTable
                  maxHeight={520}
                  rows={a.crafts}
                  empty="No drinks crafted in this period"
                  columns={[
                    { key: "at", label: "When (UTC)", render: (r) => fmtTime(r.at) },
                    { key: "recipe_name", label: "Drink", width: 220, render: (r) => r.recipe_name || "—" },
                    { key: "ingredients", label: "Ingredients", width: 320, render: (r) => (r.ingredients?.length ? r.ingredients.join(", ") : "—") },
                    { key: "machine", label: "Machine", render: (r) => r.machine || "—" },
                    { key: "device_id", label: "Machine id", render: (r) => r.device_id || "—" },
                    { key: "completed", label: "Completed", render: (r) => (r.completed === true ? "Yes" : r.completed === false ? "No" : "—") },
                    { key: "recipe_source", label: "Recipe type", render: (r) => r.recipe_source || "—" },
                  ]}
                />
              </Section>
            </div>
            <div className="col-xl-4">
              <Section title="FAVOURITE DRINKS" subtitle="most crafted" bodyClass="p-0">
                <DataTable rows={a.top_recipes} empty="—" columns={[{ key: "recipe_name", label: "Drink", width: 220 }, { key: "crafts", label: "Crafts", num: true }, { key: "last_at", label: "Last", render: (r) => fmtDate(r.last_at) }]} />
              </Section>
              <Section title="ACTIVITY SUMMARY" bodyClass="p-0">
                <DataTable rows={a.summary} empty="No activity in this period" columns={[{ key: "event", label: "Event" }, { key: "count", label: "Count", num: true }, { key: "last_at", label: "Last", render: (r) => fmtDate(r.last_at) }]} />
              </Section>
            </div>
          </div>

          <div className="row">
            <div className="col-xl-6">
              <Section title="MACHINES USED" bodyClass="p-0">
                <DataTable rows={a.machines} empty="No machines in this period" columns={[
                  { key: "machine", label: "Machine", render: (r) => r.machine || "—" },
                  { key: "device_id", label: "Machine id" },
                  { key: "crafts", label: "Crafts", num: true },
                  { key: "connections", label: "Connections", num: true },
                  { key: "first_seen", label: "First", render: (r) => fmtDate(r.first_seen) },
                  { key: "last_seen", label: "Last", render: (r) => fmtDate(r.last_seen) },
                ]} />
              </Section>
            </div>
            <div className="col-xl-6">
              <Section title="MIXLISTS" subtitle="viewed in this period" bodyClass="p-0">
                <DataTable rows={a.mixlists} empty="No mixlist views in this period" columns={[{ key: "mixlist", label: "Mixlist", width: 280, render: (r) => r.mixlist || "—" }, { key: "views", label: "Views", num: true }, { key: "last_at", label: "Last", render: (r) => fmtDate(r.last_at) }]} />
              </Section>
              {p.type === "ai" && (
                <Section title="APP SESSIONS" bodyClass="p-0">
                  <DataTable rows={a.app_sessions} empty="No app sessions in this period" columns={[
                    { key: "platform", label: "Platform" }, { key: "app_version", label: "Version" },
                    { key: "sessions", label: "Sessions", num: true }, { key: "craft_sessions", label: "Crafting", num: true },
                    { key: "barbot_sessions", label: "Barbot", num: true }, { key: "last_session", label: "Last", render: (r) => fmtDate(r.last_session) },
                  ]} />
                </Section>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-xl-7">
              <Section title="MY BAR" subtitle={d.my_bar_unavailable ? "AI app bar table not readable by the API yet" : `what they have at home${d.my_bar?.length >= 300 ? " (latest 300)" : ""}`} bodyClass="p-0">
                <DataTable
                  maxHeight={420}
                  rows={d.my_bar}
                  empty={d.my_bar_unavailable ? "Not available" : "Nothing in their bar"}
                  columns={[
                    { key: "name", label: "Item", width: 220 },
                    { key: "category", label: "Category", render: (r) => r.category || "—" },
                    { key: "detail", label: "Detail", width: 240, render: (r) => r.detail || "—" },
                    { key: "added_at", label: "Added", render: (r) => fmtDate(r.added_at) },
                  ]}
                />
              </Section>
            </div>
            <div className="col-xl-5">
              <Section title="TASTE PROFILE" subtitle={`last ${a.days} days`} bodyClass="p-0">
                <DataTable
                  maxHeight={260}
                  rows={a.taste?.top_ingredients}
                  empty="No pours in this period"
                  columns={[{ key: "ingredient", label: "Most poured ingredient", width: 220 }, { key: "crafts", label: "Drinks", num: true }]}
                />
                {p.type === "ai" && (
                  <div className="px-3 py-2 small">
                    {[["Favourite liquor", a.taste?.primary_liquor], ["Cocktail style", a.taste?.cocktail_style], ["Flavour", a.taste?.flavor_profile]].map(([label, vals]) => (
                      <div key={label} className="mb-1">
                        <span className="text-inverse text-opacity-50">{label}: </span>
                        {vals?.length ? vals.map((v) => `${v.value} (${fmtInt(v.count)})`).join(", ") : "—"}
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </div>
          </div>

          <Section title="REVIEWS & FEEDBACK" subtitle={d.reviews_unavailable?.length ? "AI app review tables not readable by the API yet" : "read directly from the app database"} bodyClass="p-0">
            <ReviewsTable rows={d.reviews} />
          </Section>
          <div className="small text-inverse text-opacity-50 mb-4">
            Activity: BigQuery unified event pipeline (~15 min lag). Profile: {p.type === "ai" ? "Ory" : "legacy app database"}. Reviews and My Bar: app databases, live. Favourite liquor / style / flavour: AI app only. Legacy and Barsys AI app accounts are separate and cannot be linked for the same person.
          </div>
        </div>
      )}
    </div>
  );
}
